// Creative-test pool and results logic (#1150, part of #533). Imports only
// services.ts and tags.ts, so the SPA, vitest, tsc and the creative agent
// (#1152, Deno) load the same module. The score itself is computed in SQL
// (the social_post_scores view); this file ranks and summarises it.
//
// The status list repeats creative_tests' CHECK; creative-tests-migration.test.ts
// keeps them equal.
import { BUDGET_SERVICES, type BudgetService } from "./services.ts";
import type { ContentTypeTag, Occasion, PrimaryService, SocialTags } from "./tags.ts";

export const CREATIVE_TEST_STATUSES = ["shadow", "testing", "winner", "extended", "loser", "dropped"] as const;
export type CreativeTestStatus = (typeof CREATIVE_TEST_STATUSES)[number];

/** A test holding one of the slots. */
export const ACTIVE_TEST_STATUSES = ["shadow", "testing", "extended"] as const satisfies readonly CreativeTestStatus[];
/** A test with a verdict. Only these count towards results. A `dropped` test (#1402) left its slot without one. */
export const DECIDED_TEST_STATUSES = ["winner", "loser"] as const satisfies readonly CreativeTestStatus[];

export const CREATIVE_TEST_STATUS_LABELS: Record<CreativeTestStatus, string> = {
  shadow: "Shadow",
  testing: "Testing",
  winner: "Winner",
  extended: "Extended",
  loser: "Loser",
  dropped: "Dropped",
};

// The social_post_scores view's eligibility rules. creative-tests-migration.test.ts
// keeps the SQL equal to these.
export const SCORE_MIN_AGE_HOURS = 48;
export const SCORE_WINDOW_DAYS = 90;
export const SCORE_MIN_REACH = 300;

export const TEST_SLOT_COUNT = 6;
/** Results hide any group with fewer finished tests than this. */
export const MIN_RESULT_SAMPLE = 5;

export function isActiveStatus(status: string): boolean {
  return (ACTIVE_TEST_STATUSES as readonly string[]).includes(status);
}

export function isDecidedStatus(status: string): boolean {
  return (DECIDED_TEST_STATUSES as readonly string[]).includes(status);
}

/** One row of the social_post_scores view, with tags already parsed (null when missing or malformed). */
export interface ScoredPost {
  social_post_id: string;
  platform: string;
  media_type: string;
  caption: string | null;
  permalink: string | null;
  thumbnail_url: string | null;
  posted_at: string;
  reach: number;
  engagement: number;
  engagement_rate: number;
  score: number;
  tags: SocialTags | null;
  /** Set when staff chose Don't test (#1561); the pool leaves the post out. */
  exclude_from_tests_at?: string | null;
}

/** A post in one service's test pool. The tag fields are top-level so #1152 can match ad copy. */
export interface PoolEntry extends Omit<ScoredPost, "tags"> {
  service: BudgetService;
  rank: number;
  services: BudgetService[];
  primary_service: PrimaryService;
  occasion: Occasion;
  content_type: ContentTypeTag;
}

/** Why a post is kept out of the test pool (#1329, #1380). */
export type PoolExclusion =
  | "skipped by staff"
  | "carousel post"
  | "unsupported media"
  | "giveaway"
  | "contest"
  | "time-limited promo"
  | "dated post"
  | "seasonal post";

/**
 * The post formats ad_from_post can copy into an ad (#1380). Equal to the
 * media check in meta-ads ad-from-post.ts loadPost; creative-tests.test.ts
 * keeps them equal. Not imported from there: this module also loads in the SPA.
 */
export const AD_MEDIA_TYPES = ["image", "video"] as const;

const MONTH =
  "(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)";
const DAY = "\\d{1,2}(?:st|nd|rd|th)?";
// m/d with an optional year (#1330). Common fractions (1/2 tray, 3/4 lb, n/8)
// and 5/5, 10/10 ratings aren't dates, and 24/7, 50/50 or 1.5/2 aren't either.
const NUMERIC_DATE =
  "(?<![\\d/.])(?!(?:1/[2348]|[23]/[34]|[357]/8|5/5|10/10)(?![\\d/]))(?:1[0-2]|0?[1-9])/(?:3[01]|[12]\\d|0?[1-9])(?:/(?:\\d{4}|\\d{2}))?(?![\\d/%])";

/**
 * Caption patterns that keep a post out of the test pool (#1329), first match
 * wins. A lead ad runs for weeks, so a giveaway, contest, limited-time offer or
 * anything with a calendar date in it would promote something that has ended,
 * with copy that asks for follows or entries instead of bookings.
 */
export const POOL_EXCLUSIONS: ReadonlyArray<{ reason: PoolExclusion; pattern: RegExp }> = [
  // No leading \b, so hashtags like #cannoligiveaway match.
  { reason: "giveaway", pattern: /give[\s-]?aways?/i },
  {
    reason: "contest",
    pattern:
      /\bcontests?\b|\bsweepstakes?\b|\braffles?\b|\bto enter\b|\benter to win\b|\bwin (?:a|an|one|two|free)\b|\bwinners?\b|\btag\s+(?:\d+|a|one|two|three|your)\b(?:\s+\w+){0,3}?\s+(?:friends?|besties?|bffs?)\b|\btag your (?:friends?|besties?|bffs?)\b|\btag someone\b|\bcomment(?:\s+\w+){0,2}?\s+to win\b|\bto be entered\b/i,
  },
  {
    reason: "time-limited promo",
    pattern:
      /\blimited[\s-]time\b|\b(?:today|tonight|this weekend|this week)\s+only\b|\b(?:offer|deal|sale|promo|special)\s+(?:ends|expires)\b|\bpromo\s+code\b|\b\d{1,2}\s?%\s?off\b|\bwhile\s+supplies\s+last\b|\buse\s+(?:the\s+)?code\b|\$\s?\d+(?:\.\d{2})?\s?off\b|\bbogo\b/i,
  },
  // Any calendar date (month name and day, either order, or numeric m/d): a dated offer or announcement.
  {
    reason: "dated post",
    pattern: new RegExp(`\\b${MONTH}\\.?\\s+${DAY}\\b|\\b${DAY}\\s+(?:of\\s+)?${MONTH}\\b|${NUMERIC_DATE}`, "i"),
  },
];

/**
 * Why a post can't be tested, or null when it can. Staff's Don't test comes
 * first (#1561). Then the format: a carousel or other post ad_from_post
 * can't copy would fail its create every day (#1380). A null media_type
 * (post row not loaded) passes this check. Then the caption; a post tagged `seasonal` (holiday, promotion
 * or dated announcement) is left out too.
 */
export function poolExclusion(post: {
  caption: string | null;
  content_type: ContentTypeTag | null;
  media_type: string | null;
  /** Staff's Don't test (#1561). Absent for held picks and executor rows, which it never affects. */
  exclude_from_tests_at?: string | null;
}): PoolExclusion | null {
  if (post.exclude_from_tests_at) return "skipped by staff";
  if (post.media_type !== null && !(AD_MEDIA_TYPES as readonly string[]).includes(post.media_type)) {
    return post.media_type === "carousel" ? "carousel post" : "unsupported media";
  }
  const caption = post.caption ?? "";
  for (const { reason, pattern } of POOL_EXCLUSIONS) if (pattern.test(caption)) return reason;
  return post.content_type === "seasonal" ? "seasonal post" : null;
}

/** A post that would sit in a service's pool but is left out, with the reason. */
export interface ExcludedPoolEntry extends Omit<PoolEntry, "rank"> {
  exclusion: PoolExclusion;
}

type PoolRankFields = Pick<PoolEntry, "primary_service" | "score" | "engagement_rate" | "posted_at" | "social_post_id">;

/**
 * Pool order: posts that fit (`fits`) first, then score, engagement rate,
 * newest, id. Service fit outranks engagement, so a post that only lists the
 * service on the side can't beat one made for it.
 */
export function comparePoolEntries<T extends PoolRankFields>(fits: (entry: T) => boolean) {
  return (a: T, b: T): number =>
    Number(fits(b)) - Number(fits(a)) ||
    b.score - a.score ||
    b.engagement_rate - a.engagement_rate ||
    b.posted_at.localeCompare(a.posted_at) ||
    a.social_post_id.localeCompare(b.social_post_id);
}

/** Pool order for one service: posts whose main service it is first. */
export function comparePoolRank(service: BudgetService) {
  return comparePoolEntries<PoolRankFields>((entry) => entry.primary_service === service);
}

/** Every untested, tagged post in each service it lists, in pool order, with its exclusion. */
function poolRows(
  scores: readonly ScoredPost[],
  testedPostIds: Iterable<string>,
): Record<BudgetService, Array<Omit<PoolEntry, "rank"> & { exclusion: PoolExclusion | null }>> {
  const tested = new Set(testedPostIds);
  const candidates = scores
    .filter((s): s is ScoredPost & { tags: SocialTags } => s.tags !== null && !tested.has(s.social_post_id))
    .map((s) => ({
      post: s,
      exclusion: poolExclusion({
        caption: s.caption,
        content_type: s.tags.content_type,
        media_type: s.media_type,
        exclude_from_tests_at: s.exclude_from_tests_at ?? null,
      }),
    }));
  const rows = {} as ReturnType<typeof poolRows>;
  for (const service of BUDGET_SERVICES) {
    rows[service] = candidates
      .filter(({ post }) => post.tags.services.includes(service))
      .map(({ post: { tags, ...post }, exclusion }) => ({
        ...post,
        service,
        services: [...tags.services],
        primary_service: tags.primary_service,
        occasion: tags.occasion,
        content_type: tags.content_type,
        exclusion,
      }))
      .sort(comparePoolRank(service));
  }
  return rows;
}

/** The eligible pools and, per service, the posts left out of them. */
export interface TestPools {
  pools: Record<BudgetService, PoolEntry[]>;
  excluded: Record<BudgetService, ExcludedPoolEntry[]>;
}

/**
 * The posts the pools leave out as already tested: every post with a
 * creative_tests row, except a `dropped` one (#1402). A dropped pick never ran
 * its test, so its post can come back in a pool that still fits it, such as a
 * re-tagged post in its new service's pool.
 */
export function testedPostIds(tests: ReadonlyArray<{ social_post_id: string; status: string }>): string[] {
  return tests.filter((t) => t.status !== "dropped").map((t) => t.social_post_id);
}

/**
 * Tags the live executor writes into a dropped row's reason (#1618), read back
 * by unusablePostIds and the admin's agent log. MEDIA_UNAVAILABLE_TAG: Meta
 * didn't return the post's media, so the post sits out UNUSABLE_POST_DAYS.
 * NO_DASHES_TAG: the ad's copy had a dash the pre-launch check refuses; no
 * cool-off, since a new ad gets cleaned copy.
 */
export const MEDIA_UNAVAILABLE_TAG = "[media_unavailable]";
export const NO_DASHES_TAG = "[no_dashes]";
export const UNUSABLE_POST_DAYS = 7;

/** Posts dropped for media_unavailable in the last UNUSABLE_POST_DAYS: left out of the pools like tested ones. */
export function unusablePostIds(
  tests: ReadonlyArray<{ social_post_id: string; status: string; reason: string | null; slot_end: string | null }>,
  nowMs: number,
): string[] {
  const since = nowMs - UNUSABLE_POST_DAYS * 86_400_000;
  return tests
    .filter((t) => t.status === "dropped" && (t.reason ?? "").includes(MEDIA_UNAVAILABLE_TAG) && t.slot_end !== null && Date.parse(t.slot_end) >= since)
    .map((t) => t.social_post_id);
}

/**
 * One ranked pool per budget service. A post sits in every pool its
 * `services` lists; untagged and general-only posts sit in none. Any post in
 * `testedPostIds` (build it with the helper above) is left out of every
 * pool. A post `poolExclusion` rejects goes to `excluded` instead, in pool
 * order. Every service key is present in both, with an empty list when
 * nothing applies.
 */
export function rankTestPools(scores: readonly ScoredPost[], testedPostIds: Iterable<string>): TestPools {
  const rows = poolRows(scores, testedPostIds);
  const result: TestPools = { pools: {} as TestPools["pools"], excluded: {} as TestPools["excluded"] };
  for (const service of BUDGET_SERVICES) {
    result.pools[service] = rows[service]
      .filter((row) => row.exclusion === null)
      .map(({ exclusion: _exclusion, ...entry }, i) => ({ ...entry, rank: i + 1 }));
    result.excluded[service] = rows[service].filter((row): row is ExcludedPoolEntry => row.exclusion !== null);
  }
  return result;
}

/** rankTestPools' eligible pools only. */
export function buildTestPools(
  scores: readonly ScoredPost[],
  testedPostIds: Iterable<string>,
): Record<BudgetService, PoolEntry[]> {
  return rankTestPools(scores, testedPostIds).pools;
}

/** A score in (0, 1] as a whole percentile, at least 1, so the top post reads 100 and none reads 0. */
export function scorePercentile(score: number): number {
  return Math.min(100, Math.max(1, Math.round(score * 100)));
}

export interface CreativeTestSnapshot {
  captured_on: string;
  spend: number | null;
  impressions: number | null;
  ctr: number | null;
  landing_page_views: number | null;
  cost_per_plan_visit: number | null;
  hook_rate: number | null;
  engagement: number | null;
  leads: number | null;
  meta_reported_leads: number | null;
}

/** Snapshots are cumulative, so the newest day is the live total. */
export function latestSnapshot<T extends { captured_on: string }>(snapshots: readonly T[]): T | null {
  let latest: T | null = null;
  for (const s of snapshots) if (!latest || s.captured_on > latest.captured_on) latest = s;
  return latest;
}

export const RESULT_DIMENSIONS = ["service", "content_type", "occasion"] as const;
export type ResultDimension = (typeof RESULT_DIMENSIONS)[number];
export const UNTAGGED = "untagged";

export interface ResultTest {
  status: string;
  service: string;
  tags: SocialTags | null;
  snapshots: readonly CreativeTestSnapshot[];
}

export interface ResultRow {
  key: string;
  n: number;
  winners: number;
  winRate: number;
  medianCtr: number | null;
  ctrN: number;
}

export interface ResultSummary {
  rows: ResultRow[];
  /** Groups left out because they have fewer than MIN_RESULT_SAMPLE finished tests. */
  hidden: number;
}

export function median(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function groupKey(test: ResultTest, dimension: ResultDimension): string {
  if (dimension === "service") return test.service;
  return test.tags?.[dimension] ?? UNTAGGED;
}

/**
 * Win rate and median CTR per group of finished tests (winner or loser).
 * n is the number of finished tests; groups with n below MIN_RESULT_SAMPLE
 * are dropped and counted in `hidden`. Groups sort by n, largest first.
 */
export function summarizeResults(tests: readonly ResultTest[], dimension: ResultDimension): ResultSummary {
  const groups = new Map<string, ResultTest[]>();
  for (const test of tests) {
    if (!isDecidedStatus(test.status)) continue;
    const key = groupKey(test, dimension);
    const group = groups.get(key);
    if (group) group.push(test);
    else groups.set(key, [test]);
  }
  const rows: ResultRow[] = [];
  let hidden = 0;
  for (const [key, group] of groups) {
    if (group.length < MIN_RESULT_SAMPLE) {
      hidden += 1;
      continue;
    }
    const winners = group.filter((t) => t.status === "winner").length;
    const ctrs = group
      .map((t) => latestSnapshot(t.snapshots)?.ctr)
      .filter((c): c is number => typeof c === "number");
    rows.push({ key, n: group.length, winners, winRate: winners / group.length, medianCtr: median(ctrs), ctrN: ctrs.length });
  }
  rows.sort((a, b) => b.n - a.n || a.key.localeCompare(b.key));
  return { rows, hidden };
}
